export type AutoReconnectSkipParams = {
  resumeInFlight: boolean;
  isConnecting: boolean;
  isStopping: boolean;
  isPausing: boolean;
};

export type RecordingOperationFlags = AutoReconnectSkipParams;

export type ResumePausedScribeFailureReason =
  | "stop"
  | "pause"
  | "connect"
  | "in_flight"
  | "timeout"
  | "missing_params"
  | "start_failed";

export type ResumePausedScribeResult =
  | { ok: true }
  | { ok: false; reason: ResumePausedScribeFailureReason };

/** Whether the auto-reconnect timer should skip firing startRecordingInternal. */
export const shouldSkipAutoReconnect = ({
  resumeInFlight,
  isConnecting,
  isStopping,
  isPausing
}: AutoReconnectSkipParams): boolean =>
  resumeInFlight || isConnecting || isStopping || isPausing;

/** Whether a debounced interim transcript update should be dispatched to Redux. */
export const shouldDispatchInterimTranscript = (
  isRecording: boolean
): boolean => isRecording;

/** True when a newer pause/stop/resume bumped pauseCleanupGenerationRef. */
export const isStopSuperseded = (
  capturedGeneration: number,
  currentGeneration: number
): boolean => capturedGeneration !== currentGeneration;

export const isRecordingOperationBusy = (
  flags: RecordingOperationFlags
): boolean => shouldSkipAutoReconnect(flags);

export const getRecordingOperationBusyReason = (
  flags: RecordingOperationFlags
): ResumePausedScribeFailureReason | undefined => {
  if (flags.resumeInFlight) {
    return "in_flight";
  }
  if (flags.isStopping) {
    return "stop";
  }
  if (flags.isPausing) {
    return "pause";
  }
  if (flags.isConnecting) {
    return "connect";
  }
  return undefined;
};

export type WaitForRecordingOperationIdleParams = {
  getFlags: () => RecordingOperationFlags;
  timeoutMs?: number;
  pollMs?: number;
  wait?: (ms: number) => Promise<void>;
};

/** Poll until recording ops are idle or timeout elapses. */
export const waitForRecordingOperationIdle = async ({
  getFlags,
  timeoutMs = 15000,
  pollMs = 100,
  wait: waitFn = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
}: WaitForRecordingOperationIdleParams): Promise<boolean> => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (!isRecordingOperationBusy(getFlags())) {
      return true;
    }
    await waitFn(pollMs);
  }
  return !isRecordingOperationBusy(getFlags());
};

export type WaitForBackgroundCleanupIdleParams = {
  getBackgroundCleanupInFlight: () => boolean;
  timeoutMs?: number;
  pollMs?: number;
  wait?: (ms: number) => Promise<void>;
};

/**
 * Poll until background pause/stop cleanup finishes or timeout elapses.
 * Callers may proceed after this returns — timeout is a cap, not a hard failure.
 */
export const waitForBackgroundCleanupIdle = async ({
  getBackgroundCleanupInFlight,
  /** Matches BACKGROUND_CLEANUP_WAIT_MS in streaming/constants.ts */
  timeoutMs = 3000,
  pollMs = 100,
  wait: waitFn = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
}: WaitForBackgroundCleanupIdleParams): Promise<void> => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline && getBackgroundCleanupInFlight()) {
    await waitFn(pollMs);
  }
};
